import React, { useState } from 'react';
import { RefreshCw, Bell, ShieldCheck, Check, Archive, UserPlus, KeyRound, Mail } from 'lucide-react';

const NOTIF_TYPE_CONFIG = {
  verification_approved: { icon: ShieldCheck, color: 'bg-green-500/15', iconColor: 'text-green-400', label: 'Persona' },
  new_account: { icon: UserPlus, color: 'bg-blue-500/15', iconColor: 'text-blue-400', label: 'New Account' },
  password_change: { icon: KeyRound, color: 'bg-amber-500/15', iconColor: 'text-amber-400', label: 'Password Change' },
  email_update: { icon: Mail, color: 'bg-purple-500/15', iconColor: 'text-purple-400', label: 'Email Update' },
};

const NotifIcon = ({ type }) => {
  const cfg = NOTIF_TYPE_CONFIG[type] || { icon: Bell, color: 'bg-blue-500/15', iconColor: 'text-blue-400' };
  const Icon = cfg.icon;
  return (
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
      <Icon className={`h-4 w-4 ${cfg.iconColor}`} />
    </div>
  );
};

const NotifList = ({ items, onMarkRead, emptyMessage }) => {
  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <Bell className="h-10 w-10 text-slate-600 mx-auto mb-2" />
        <p className="text-slate-400 text-sm">{emptyMessage}</p>
      </div>
    );
  }
  return (
    <div className="divide-y divide-white/5">
      {items.map((notif) => (
        <div key={notif.id} className={`px-4 py-4 hover:bg-white/5 transition-colors ${!notif.read ? 'bg-blue-500/5 border-l-2 border-blue-500' : ''}`} data-testid={`notif-${notif.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <NotifIcon type={notif.type} />
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${(NOTIF_TYPE_CONFIG[notif.type] || {}).color || 'bg-slate-500/15'} ${(NOTIF_TYPE_CONFIG[notif.type] || {}).iconColor || 'text-slate-400'}`}>
                    {(NOTIF_TYPE_CONFIG[notif.type] || {}).label || notif.type}
                  </span>
                </div>
                <p className={`text-sm ${notif.read ? 'text-slate-400' : 'text-white font-medium'}`}>{notif.message}</p>
                <p className="text-xs text-slate-500 mt-1">{new Date(notif.created_at).toLocaleString()}</p>
              </div>
            </div>
            {!notif.read && onMarkRead && (
              <button onClick={() => onMarkRead(notif.id)} className="p-1.5 text-slate-400 hover:text-blue-400 rounded transition-colors flex-shrink-0" title="Mark as read" data-testid={`mark-read-${notif.id}`}>
                <Check className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export const NotificationSection = ({ notifications, archivedNotifs = [], notifLoading, unreadCount, onMarkRead }) => {
  const [activeView, setActiveView] = useState('recent');
  const [archiveFilter, setArchiveFilter] = useState('all');

  const filteredArchive = archiveFilter === 'all'
    ? archivedNotifs
    : archivedNotifs.filter(n => n.type === archiveFilter);

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid="notifications-section">
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveView('recent')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeView === 'recent' ? 'bg-blue-600 text-white' : 'bg-white/5 text-slate-400 hover:text-white'}`}
            data-testid="notif-tab-recent"
          >
            <span className="flex items-center gap-1.5">
              <Bell className="h-3.5 w-3.5" />Notifications
              {unreadCount > 0 && <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5 min-w-[16px] text-center">{unreadCount}</span>}
            </span>
          </button>
          <button
            onClick={() => setActiveView('archive')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeView === 'archive' ? 'bg-blue-600 text-white' : 'bg-white/5 text-slate-400 hover:text-white'}`}
            data-testid="notif-tab-archive"
          >
            <span className="flex items-center gap-1.5">
              <Archive className="h-3.5 w-3.5" />Archives
              {archivedNotifs.length > 0 && <span className="text-slate-500 text-[10px]">({archivedNotifs.length})</span>}
            </span>
          </button>
        </div>
      </div>

      {notifLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : activeView === 'recent' ? (
        <>
          <NotifList items={notifications} onMarkRead={onMarkRead} emptyMessage="No recent notifications" />
          <div className="px-4 py-3 border-t border-white/10 text-slate-400 text-xs">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All read'} · {notifications.length} recent
          </div>
        </>
      ) : (
        <>
          {/* Archive category filter */}
          <div className="px-4 py-2 border-b border-white/5 flex gap-2 flex-wrap">
            {[
              { id: 'all', label: 'All' },
              { id: 'verification_approved', label: 'Persona' },
              { id: 'new_account', label: 'New Account' },
              { id: 'password_change', label: 'Password Change' },
              { id: 'email_update', label: 'Email Update' },
            ].map(f => (
              <button key={f.id} onClick={() => setArchiveFilter(f.id)}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${archiveFilter === f.id ? 'bg-blue-600 text-white' : 'bg-white/5 text-slate-400 hover:text-white'}`}
                data-testid={`archive-filter-${f.id}`}>
                {f.label}
              </button>
            ))}
          </div>
          <NotifList items={filteredArchive} emptyMessage="No archived notifications" />
          <div className="px-4 py-3 border-t border-white/10 text-slate-400 text-xs">
            {filteredArchive.length} archived {archiveFilter !== 'all' ? `(${archiveFilter.replace('_', ' ')})` : ''}
          </div>
        </>
      )}
    </div>
  );
};
